import { FieldValues } from 'react-hook-form';
import { AnyMemo } from 'models/Memo';
import type { MemoPayload } from 'networking/mindfeed/models/Memo';
import { useSaveMemo, Variables } from 'networking/mindfeed/hooks/useSaveMemo';
import {
  extractSharedMemoFields,
  transformSpecificMemoFields,
} from '../transformers/memoFormTransformer';

interface SubmitFormProps {
  onSuccess?: () => void;
  // The day a new memo is dated to when the user hasn't picked one, e.g.
  // the Journal day that is open. Ignored when editing.
  defaultDatedAt?: Date | null;
}

export function useFormSubmission({
  onSuccess,
  defaultDatedAt,
}: SubmitFormProps) {
  const { isPending, mutate } = useSaveMemo({
    onSuccess: (_data: AnyMemo) => {
      onSuccess?.();
    },
  });

  const onSubmit = async (data: FieldValues) => {
    const shared = extractSharedMemoFields(data);
    const specific = transformSpecificMemoFields(data);

    const memo: MemoPayload = { ...shared, ...specific };

    // No date picked for a new memo: use the contextual default, if any.
    if (!memo.id && !memo.datedAt && defaultDatedAt) {
      memo.datedAt = defaultDatedAt.toISOString();
    }

    const variables = { memo } as Variables;

    mutate(variables);
  };

  return {
    isSubmitting: isPending,
    onSubmit,
  };
}
